import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";

const statusColor = {
  requested: "warning",
  contacted: "info",
  closed: "success",
};

const OpportunityContactRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/opportunity-companion/admin/contact-requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests(response.data?.requests || []);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to load Opportunity Companion contact requests."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/opportunity-companion/admin/contact-requests/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests((current) =>
        current.map((request) =>
          request.id === id ? { ...request, status } : request
        )
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to update the contact request."
      );
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Opportunity Companion Contacts
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Registered members asking UC for a mentor, program, community, or
        enterprise introduction.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!requests.length ? (
        <Typography>No Opportunity Companion contact requests yet.</Typography>
      ) : (
        <Stack spacing={2}>
          {requests.map((request) => (
            <Box
              key={request.id}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: "background.paper",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={800}>
                    {request.first_name} {request.last_name}
                  </Typography>
                  <Typography variant="body2">
                    {request.email} · {request.mobile || "No mobile"}
                  </Typography>
                </Box>
                <Chip
                  label={request.status}
                  color={statusColor[request.status] || "default"}
                  size="small"
                />
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                  gap: 1,
                  my: 2,
                }}
              >
                <Typography variant="body2">
                  <strong>Primary:</strong> {request.primary_sector}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong> {request.roi_time || "Not provided"}
                </Typography>
                <Typography variant="body2">
                  <strong>Early income:</strong> {request.roi_income || "Not provided"}
                </Typography>
              </Box>

              {request.notes && (
                <Typography
                  variant="body2"
                  sx={{ p: 1.25, mb: 2, borderRadius: 1, backgroundColor: "grey.50" }}
                >
                  <strong>Member note:</strong> {request.notes}
                </Typography>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="contained"
                  disabled={updatingId === request.id || request.status === "contacted"}
                  onClick={() => updateStatus(request.id, "contacted")}
                >
                  Mark contacted
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={updatingId === request.id || request.status === "closed"}
                  onClick={() => updateStatus(request.id, "closed")}
                >
                  Close request
                </Button>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default OpportunityContactRequests;
